if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.handleResize = debounce(this.updateActiveMediaHeight.bind(this), 120);

        this.elements.viewer?.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 80));
        window.addEventListener('resize', this.handleResize);
        requestAnimationFrame(() => this.updateActiveMediaHeight());

        if (!this.elements.thumbnails) return;

        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      disconnectedCallback() {
        window.removeEventListener('resize', this.handleResize);
      }

      onSlideChanged(event) {
        const currentMedia = event.detail.currentElement;
        if (!currentMedia) return;

        this.elements.viewer
          ?.querySelectorAll('[data-media-id]')
          .forEach((element) => element.classList.toggle('is-active', element === currentMedia));

        this.updateActiveMediaHeight(currentMedia);

        if (!this.elements.thumbnails) return;
        const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${currentMedia.dataset.mediaId}"]`);
        this.setActiveThumbnail(thumbnail);
      }

      updateActiveMediaHeight(activeMedia = null) {
        const mediaList = this.elements.viewer?.querySelector('[id^="Slider-Gallery"]');
        if (!mediaList) return;

        if (this.mql.matches) {
          mediaList.style.removeProperty('height');
          mediaList.classList.remove('rs-product-media-auto-height');
          return;
        }

        const currentMedia =
          activeMedia || mediaList.querySelector('.product__media-item.is-active') || mediaList.querySelector('[data-media-id]');
        if (!currentMedia) return;

        const applyHeight = () => {
          // Important: enable flex-start BEFORE measuring. Otherwise a slide can be stretched
          // to the height of the tallest media item and create a large blank area below the image.
          mediaList.classList.add('rs-product-media-auto-height');
          mediaList.style.removeProperty('height');

          requestAnimationFrame(() => {
            const mediaBox = currentMedia.querySelector('.product-media-container') || currentMedia.firstElementChild;
            const height = mediaBox?.getBoundingClientRect().height || currentMedia.scrollHeight;
            if (!height) return;
            mediaList.style.height = `${Math.ceil(height)}px`;
          });
        };

        applyHeight();

        const image = currentMedia.querySelector('img');
        if (image && !image.complete) image.addEventListener('load', applyHeight, { once: true });
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');
        this.updateActiveMediaHeight(activeMedia);

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
